import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BottomNav } from '../components/BottomNav';
import { toast } from '../utils/toast';
import type { EmploymentStatus } from '../types';

interface BulkEmployeeData {
  NIP?: string;
  Nama: string;
  Jabatan?: string;
  Status_Pekerjaan: EmploymentStatus;
  No_Telp?: string;
  Tanggal_Lahir?: string;
  Jenis_Kelamin?: string;
  Kecamatan?: string;
  Kota?: string;
  Provinsi?: string;
  KTP?: string;
  Status_Kawin?: string;
  Kontak_Darurat_Nama?: string;
  Kontak_Darurat_Hubungan?: string;
  Kontak_Darurat_Telp?: string;
  Kontak_Darurat_Alamat?: string;
  company_id?: string;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

export const EmployeeBulkImport: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [employees, setEmployees] = useState<BulkEmployeeData[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const downloadTemplate = () => {
    // Required fields first
    const requiredHeaders = [
      'NIP',
      'Nama',
      'Jabatan',
      'No_Telp',
      'Tanggal_Lahir',
      'Jenis_Kelamin',
      'Provinsi',
      'Kota',
      'Kecamatan',
      'KTP'
    ];

    // Optional fields at the end
    const optionalHeaders = [
      'Status_Kawin',
      'Kontak_Darurat_Nama',
      'Kontak_Darurat_Hubungan',
      'Kontak_Darurat_Telp',
      'Kontak_Darurat_Alamat'
    ];

    const headers = [...requiredHeaders, ...optionalHeaders];

    const exampleData = [
      [
        'EMP001',
        'John Doe',
        'Manager',
        '081234567890',
        'DD-MM-YYYY',
        'Laki-laki',
        'DKI Jakarta',
        'Jakarta Selatan',
        'Kebayoran Baru',
        '3171234567890123',
        'Kawin',
        'Jane Doe',
        'Spouse',
        '081234567891',
        'Jl. Example No. 123'
      ]
    ];

    const csvContent = [
      headers.join(','),
      ...exampleData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'employee_bulk_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Template downloaded successfully');
  };

  const parseCSV = (text: string): string[][] => {
    const lines = text.split('\n');
    const result: string[][] = [];
    
    // Detect delimiter (comma or semicolon)
    let delimiter = ',';
    if (lines.length > 0) {
      const firstLine = lines[0];
      // Count commas and semicolons in first line
      const commaCount = (firstLine.match(/,/g) || []).length;
      const semicolonCount = (firstLine.match(/;/g) || []).length;
      
      // Use semicolon if it appears more frequently
      if (semicolonCount > commaCount) {
        delimiter = ';';
      }
    }
    
    for (const line of lines) {
      if (!line.trim()) continue;
      
      const row: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          row.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      row.push(current.trim());
      result.push(row);
    }
    
    return result;
  };

  // Helper function to convert scientific notation to regular number
  const convertScientificNotation = (value: string): string => {
    if (!value || !value.trim()) return value;
    
    const trimmed = value.trim();
    
    // Check if value is in scientific notation (e.g., 3,17123E+15 or 3.17123E+15)
    const scientificRegex = /^[\d,\.]+[eE][+-]?\d+$/;
    
    if (scientificRegex.test(trimmed)) {
      try {
        // Replace comma with dot for parsing
        const normalized = trimmed.replace(',', '.');
        const num = parseFloat(normalized);
        
        // Convert to integer string without decimal point
        if (Number.isInteger(num)) {
          return Math.round(num).toString();
        } else {
          // If it has decimals, convert to string with full precision
          return num.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 0 });
        }
      } catch (e) {
        return trimmed;
      }
    }
    
    return trimmed;
  };

  const validateEmployeeData = (data: BulkEmployeeData[], startRow: number): ValidationError[] => {
    const errors: ValidationError[] = [];
    const validStatuses: EmploymentStatus[] = ['active', 'konseling', 'sp1', 'sp2', 'sp3', 'inactive'];
    
    data.forEach((employee, index) => {
      const rowNum = startRow + index;
      
      // Required: Nama
      if (!employee.Nama || !employee.Nama.trim()) {
        errors.push({
          row: rowNum,
          field: 'Nama',
          message: 'Name is required'
        });
      }
      
      // Validate date format if provided
      if (employee.Tanggal_Lahir && employee.Tanggal_Lahir.trim()) {
        const dateStr = employee.Tanggal_Lahir.trim();
        // Accept multiple date formats: DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD
        const dateRegex = /^(\d{2}-\d{2}-\d{4}|\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/;
        
        if (!dateRegex.test(dateStr)) {
          errors.push({
            row: rowNum,
            field: 'Tanggal_Lahir',
            message: 'Invalid date format. Use DD-MM-YYYY, DD/MM/YYYY, or YYYY-MM-DD'
          });
        } else {
          // Validate date is valid
          let date: Date | null = null;
          
          if (dateStr.includes('-') && dateStr.startsWith('19') || dateStr.startsWith('20')) {
            // YYYY-MM-DD format
            date = new Date(dateStr);
          } else if (dateStr.includes('/')) {
            // DD/MM/YYYY format
            const parts = dateStr.split('/');
            date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          } else if (dateStr.includes('-') && !dateStr.startsWith('19') && !dateStr.startsWith('20')) {
            // DD-MM-YYYY format
            const parts = dateStr.split('-');
            date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          }
          
          if (date && isNaN(date.getTime())) {
            errors.push({
              row: rowNum,
              field: 'Tanggal_Lahir',
              message: 'Invalid date value'
            });
          }
        }
      }
      
      // Validate gender if provided
      if (employee.Jenis_Kelamin && employee.Jenis_Kelamin.trim()) {
        const validGenders = ['Laki-laki', 'Perempuan'];
        if (!validGenders.includes(employee.Jenis_Kelamin)) {
          errors.push({
            row: rowNum,
            field: 'Jenis_Kelamin',
            message: 'Gender must be "Laki-laki" or "Perempuan"'
          });
        }
      }
      
      // Validate marital status if provided
      if (employee.Status_Kawin && employee.Status_Kawin.trim()) {
        const validStatuses = ['Belum Kawin', 'Kawin', 'Cerai'];
        if (!validStatuses.includes(employee.Status_Kawin)) {
          errors.push({
            row: rowNum,
            field: 'Status_Kawin',
            message: 'Marital status must be "Belum Kawin", "Kawin", or "Cerai"'
          });
        }
      }

      // Validate employment status if provided
      if (employee.Status_Pekerjaan && employee.Status_Pekerjaan.trim()) {
        if (!validStatuses.includes(employee.Status_Pekerjaan)) {
          errors.push({
            row: rowNum,
            field: 'Status_Pekerjaan',
            message: `Invalid status. Must be one of: ${validStatuses.join(', ')}`
          });
        }
      }
      // Note: Status_Pekerjaan will default to 'active' if not provided
    });
    
    return errors;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);
    setLoading(true);
    setValidationErrors([]);
    setEmployees([]);
    setShowPreview(false);

    try {
      const text = await selectedFile.text();
      const rows = parseCSV(text);
      
      if (rows.length < 2) {
        toast.error('CSV file must contain header and at least one data row');
        setLoading(false);
        return;
      }

      const headers = rows[0].map(h => h.replace(/"/g, '').trim());
      const dataRows = rows.slice(1);

      const parsedEmployees: BulkEmployeeData[] = dataRows.map(row => {
        const employee: any = {};
        headers.forEach((header, index) => {
          let value = row[index]?.replace(/"/g, '').trim();
          
          // Convert scientific notation for numeric fields
          if (value && ['KTP', 'NIP', 'No_Telp'].includes(header)) {
            value = convertScientificNotation(value);
          }
          
          employee[header] = value || undefined;
        });
        return employee;
      });

      // Validate data
      const errors = validateEmployeeData(parsedEmployees, 2); // Start from row 2 (after header)
      setValidationErrors(errors);
      setEmployees(parsedEmployees);
      setShowPreview(true);

      if (errors.length === 0) {
        toast.success(`Successfully parsed ${parsedEmployees.length} employees`);
      } else {
        toast.error(`Found ${errors.length} validation errors`);
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast.error('Failed to parse CSV file');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (validationErrors.length > 0) {
      toast.error('Please fix validation errors before importing');
      return;
    }

    if (employees.length === 0) {
      toast.error('No employees to import');
      return;
    }

    setImporting(true);

    try {
      // Helper function to normalize date to YYYY-MM-DD format
      const normalizeDate = (dateStr: string | undefined): string | undefined => {
        if (!dateStr || !dateStr.trim()) return undefined;
        
        const trimmed = dateStr.trim();
        
        // Already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          return trimmed;
        }
        
        // DD/MM/YYYY format
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
          const parts = trimmed.split('/');
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        
        // DD-MM-YYYY format (primary format)
        if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
          const parts = trimmed.split('-');
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        
        return undefined;
      };

      // Transform data to match API format
      const payload = employees.map(emp => {
        const data: any = {
          NIP: emp.NIP || undefined,
          Nama: emp.Nama,
          Jabatan: emp.Jabatan || undefined,
          Status_Pekerjaan: emp.Status_Pekerjaan || 'active', // Default to 'active' if not provided
          No_Telp: emp.No_Telp || undefined,
          Tanggal_Lahir: normalizeDate(emp.Tanggal_Lahir),
          Jenis_Kelamin: emp.Jenis_Kelamin || undefined,
          Provinsi: emp.Provinsi || undefined,
          Kota: emp.Kota || undefined,
          Kecamatan: emp.Kecamatan || undefined,
          KTP: emp.KTP || undefined,
          Status_Kawin: emp.Status_Kawin || undefined,
          // company_id is auto-assigned by backend, no need to include
        };

        // Add emergency contact if any field is provided
        if (emp.Kontak_Darurat_Nama || emp.Kontak_Darurat_Hubungan || 
            emp.Kontak_Darurat_Telp || emp.Kontak_Darurat_Alamat) {
          data.Kontak_Darurat = {
            name: emp.Kontak_Darurat_Nama || '',
            relationship: emp.Kontak_Darurat_Hubungan || '',
            phone: emp.Kontak_Darurat_Telp || '',
            address: emp.Kontak_Darurat_Alamat || '',
          };
        }

        return data;
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3031/api'}/employees/bulk`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: JSON.stringify({ employees: payload }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to import employees');
      }

      const result = await response.json();
      toast.success(`Successfully imported ${result.data.successful} employees`);
      
      if (result.data.failed > 0) {
        toast.error(`Failed to import ${result.data.failed} employees`);
      }

      // Reset form
      setFile(null);
      setEmployees([]);
      setShowPreview(false);
      setValidationErrors([]);
      
      // Navigate back to employee list
      setTimeout(() => {
        navigate('/employees');
      }, 2000);
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(error.message || 'Failed to import employees');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar />
      <Header title="Bulk Import Employees" />
      
      <main className="ml-0 md:ml-64 pt-20 p-6 flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className='flex justify-between'>
            <div className="mb-6 col">
              <h1 className="text-3xl font-bold text-gray-900">Bulk Import Employees</h1>
              <p className="text-gray-600 mt-2">Upload a CSV file to import multiple employees at once</p>
            </div>
            <div className="mt-4 col">
              <button
                onClick={downloadTemplate}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                📥 Download CSV Template
              </button>
            </div>
          </div>

          {/* Upload Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload CSV File</h3>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading || importing}
              />
              {file && (
                <span className="text-sm text-gray-600">
                  {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </span>
              )}
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-red-900 mb-3">
                Validation Errors ({validationErrors.length})
              </h3>
              <div className="max-h-60 overflow-y-auto">
                <table className="min-w-full divide-y divide-red-200">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900">Row</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900">Field</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-200">
                    {validationErrors.map((error, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-red-800">{error.row}</td>
                        <td className="px-4 py-2 text-sm text-red-800">{error.field}</td>
                        <td className="px-4 py-2 text-sm text-red-800">{error.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {showPreview && employees.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm flex-1 flex flex-col overflow-hidden">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  Preview ({employees.length} employees)
                </h3>
              </div>
              <div className="flex-1 overflow-auto p-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Birth Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Province</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">KTP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marital</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employees.map((employee, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{employee.NIP || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{employee.Nama}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.Jabatan || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.No_Telp || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.Tanggal_Lahir || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.Jenis_Kelamin || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.Provinsi || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.Kota || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.Kecamatan || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.KTP || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{employee.Status_Kawin || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Action Buttons */}
              <div className="p-6 border-t bg-gray-50 flex gap-4">
                <button
                  onClick={() => navigate('/employees')}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={validationErrors.length > 0 || importing}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importing ? 'Importing...' : 'Import Employees'}
                </button>
              </div>
            </div>
          )}

          <Footer />
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default EmployeeBulkImport;
